import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import { db } from "../../../firebase/firebase";
import {
  getCurrentUser,
  approveAccessRequestAccount,
} from "../../../utils/auth";

const STATUS_CLASS_MAP = {
  pending: "ac-dash-statusbadge is-warm",
  approved: "ac-dash-statusbadge is-soft",
  rejected: "ac-dash-statusbadge is-info",
};

const STATUS_LABEL_MAP = {
  pending: "Ausstehend",
  approved: "Genehmigt",
  rejected: "Abgelehnt",
};

function StatusPill({ status }) {
  return (
    <span className={STATUS_CLASS_MAP[status] || "ac-dash-statusbadge"}>
      {STATUS_LABEL_MAP[status] || status}
    </span>
  );
}

function formatRole(role) {
  if (role === "teacher") return "Lehrkraft";
  if (role === "viewer") return "Einsicht / Sonstiges";
  return role || "—";
}

export default function AccessRequestsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [busyId, setBusyId] = useState("");

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "accessRequests"), orderBy("createdAt", "desc"))
      );

      const data = snap.docs.map((entry) => ({
        id: entry.id,
        ...entry.data(),
      }));

      setItems(data);
    } catch (error) {
      console.error("Zugangsanfragen konnten nicht geladen werden:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const filteredItems = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((item) => item.status === filter);
  }, [items, filter]);

  const summary = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        if (item.status === "pending") acc.pending += 1;
        else if (item.status === "approved") acc.approved += 1;
        else if (item.status === "rejected") acc.rejected += 1;
        return acc;
      },
      { pending: 0, approved: 0, rejected: 0 }
    );
  }, [items]);

  async function approveRequest(item) {
    setBanner({ type: "", text: "" });
    setBusyId(item.id);

    try {
      const reviewer = getCurrentUser() || null;
      const result = await approveAccessRequestAccount(item, reviewer);

      if (!result.ok) {
        setBanner({
          type: "error",
          text: result.error || "Die Anfrage konnte nicht genehmigt werden.",
        });
        setBusyId("");
        return;
      }

      setBanner({
        type: result.activationMailSent ? "success" : "warning",
        text: result.activationMailSent
          ? "Zugang genehmigt. Das Login-Konto wurde vorbereitet und die Passwort-Mail wurde gesendet."
          : result.warning ||
            "Zugang genehmigt, aber die Passwort-Mail konnte nicht automatisch gesendet werden.",
      });

      await loadRequests();
    } catch (error) {
      console.error("Anfrage konnte nicht genehmigt werden:", error);
      setBanner({
        type: "error",
        text: "Die Anfrage konnte nicht genehmigt werden.",
      });
    } finally {
      setBusyId("");
    }
  }

  async function rejectRequest(item) {
    setBanner({ type: "", text: "" });
    setBusyId(item.id);

    try {
      await updateDoc(doc(db, "accessRequests", item.id), {
        status: "rejected",
        reviewedAt: serverTimestamp(),
        onboardingState: "rejected",
        passwordSetupStatus: "not-available",
      });

      await setDoc(
        doc(db, "adminLogs", `access-${item.id}`),
        {
          type: "access-request-rejected",
          requestId: item.id,
          email: item.email || "",
          name: item.name || "",
          role: item.role || "",
          requestedClasses: item.requestedClasses || [],
          reviewedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setBanner({
        type: "success",
        text: "Anfrage wurde abgelehnt.",
      });

      await loadRequests();
    } catch (error) {
      console.error("Anfrage konnte nicht abgelehnt werden:", error);
      setBanner({
        type: "error",
        text: "Die Anfrage konnte nicht abgelehnt werden.",
      });
    } finally {
      setBusyId("");
    }
  }

  return (
    <DashboardLayout title="Zugangsanfragen">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-noti-hero ac-noti-hero-compact">
          <div className="ac-noti-hero-grid">
            <div>
              <h2 className="ac-noti-hero-title">Zugangsanfragen verwalten</h2>
              <p className="ac-noti-hero-text">
                Hier prüfst du neue Zugangs-Anfragen für Lehrkräfte oder Einsicht.
                Lehrkräfte bekommen nach der Freigabe nur Zugriff auf ihre angefragte
                Klasse und erhalten automatisch eine Passwort-Mail.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div className="ac-soft-panel ac-dash-block">
                  <div className="ac-dash-block-title">Ausstehend</div>
                  <div className="ac-dash-kpi-value">{summary.pending}</div>
                </div>

                <div className="ac-soft-panel ac-dash-block">
                  <div className="ac-dash-block-title">Genehmigt</div>
                  <div className="ac-dash-kpi-value">{summary.approved}</div>
                </div>

                <div className="ac-soft-panel ac-dash-block">
                  <div className="ac-dash-block-title">Abgelehnt</div>
                  <div className="ac-dash-kpi-value">{summary.rejected}</div>
                </div>
              </div>
            </div>

            <div className="ac-noti-hero-side">
              <div className="ac-noti-acebox ac-noti-acebox-compact">
                <AceAvatar variant="ruhig1" size={56} />
                <div className="ac-noti-acebubble ac-noti-acebubble-compact">
                  Ace sagt: Offene Anfragen solltest du zeitnah prüfen, damit der Zugang
                  sauber und nachvollziehbar bleibt.
                </div>
              </div>
            </div>
          </div>
        </section>

        {banner.text ? (
          <section
            className={banner.type === "error" ? "ac-login-error" : "ac-login-success"}
          >
            <span className="ac-login-erroricon">
              {banner.type === "error" ? "!" : banner.type === "warning" ? "i" : "✓"}
            </span>
            <span>{banner.text}</span>
          </section>
        ) : null}

        <section className="ac-soft-panel p-4 sm:p-5">
          <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ac-select w-full"
            >
              <option value="pending">Ausstehend</option>
              <option value="approved">Genehmigt</option>
              <option value="rejected">Abgelehnt</option>
              <option value="all">Alle</option>
            </select>

            <div className="ac-dash-block-subtitle" style={{ alignSelf: "center" }}>
              {loading
                ? "Anfragen werden geladen..."
                : `${filteredItems.length} Anfrage(n) in dieser Ansicht`}
            </div>
          </div>
        </section>

        <section className="grid gap-3">
          {filteredItems.length === 0 ? (
            <div className="ac-soft-panel ac-noti-empty">
              <h3 className="ac-noti-empty-title">Keine Anfragen</h3>
              <p className="ac-noti-empty-text">
                In diesem Statusbereich liegen aktuell keine Zugangsanfragen vor.
              </p>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div key={item.id} className="ac-soft-panel ac-noti-card">
                <div className="ac-noti-card-inner">
                  <div className="ac-noti-card-left">
                    <div className="ac-noti-card-icon">
                      <AceAvatar variant="motiviert1" size={36} />
                    </div>

                    <div className="min-w-0">
                      <div className="ac-noti-card-head">
                        <h3 className="ac-noti-card-title">{item.name || "Unbekannt"}</h3>
                        <StatusPill status={item.status} />
                      </div>

                      <p className="ac-noti-card-text">
                        <strong>E-Mail:</strong> {item.email || "—"}
                        <br />
                        <strong>Rolle:</strong> {formatRole(item.role)}
                        <br />
                        <strong>Schule:</strong> {item.schoolName || "—"}
                        <br />
                        <strong>Klasse:</strong>{" "}
                        {Array.isArray(item.requestedClasses)
                          ? item.requestedClasses.join(", ")
                          : "—"}
                        <br />
                        <strong>Nachricht:</strong> {item.message || "—"}
                        <br />
                        <strong>Passwort-Setup:</strong>{" "}
                        {item.passwordSetupStatus === "email-sent"
                          ? "Mail gesendet"
                          : item.passwordSetupStatus === "email-failed"
                          ? "Mail fehlgeschlagen"
                          : item.passwordSetupStatus === "not-sent"
                          ? "Wartet auf Freigabe"
                          : "—"}
                      </p>
                    </div>
                  </div>

                  <div className="ac-noti-card-right">
                    {item.status === "pending" ? (
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          className="ac-primarybtn ac-noti-actionbtn"
                          onClick={() => approveRequest(item)}
                          disabled={busyId === item.id}
                        >
                          {busyId === item.id ? "Bitte warten..." : "Genehmigen"}
                        </button>

                        <button
                          type="button"
                          className="ac-ghostbtn ac-noti-actionbtn"
                          onClick={() => rejectRequest(item)}
                          disabled={busyId === item.id}
                        >
                          Ablehnen
                        </button>
                      </div>
                    ) : (
                      <div className="ac-noti-card-time">
                        Anfrage bereits bearbeitet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}